import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface FavoriteShop {
  id: string;
  user_id: string;
  shop_id: string;
  created_at: string;
}

interface FavoriteShopsState {
  favorites: FavoriteShop[];
  isLoading: boolean;
  
  /** Fetch all favorites for a user */
  fetchFavorites: (userId: string) => Promise<void>;
  
  /** Add a new favorite */
  addFavorite: (userId: string, shopId: string) => Promise<void>;
  
  /** Remove an existing favorite */
  removeFavorite: (userId: string, shopId: string) => Promise<void>;
  
  /** Check if a shop is favorited */
  isFavorite: (shopId: string) => boolean;
}

export const useFavoriteShopsStore = create<FavoriteShopsState>((set, get) => ({
  favorites: [],
  isLoading: false,

  fetchFavorites: async (userId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('favorite_shops')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      set({ favorites: data || [] });
    } catch (error) {
      console.error('Error fetching favorite shops:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  addFavorite: async (userId: string, shopId: string) => {
    try {
      const { data, error } = await supabase
        .from('favorite_shops')
        .insert({
          user_id: userId,
          shop_id: shopId,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (data) {
        set((state) => ({
          favorites: [...state.favorites, data as FavoriteShop],
        }));
      }
    } catch (error) {
      console.error('Error adding favorite shop:', error);
    }
  },

  removeFavorite: async (userId: string, shopId: string) => {
    try {
      const { error } = await supabase
        .from('favorite_shops')
        .delete()
        .eq('user_id', userId)
        .eq('shop_id', shopId);

      if (error) throw error;
      set((state) => ({
        favorites: state.favorites.filter((f) => f.shop_id !== shopId),
      }));
    } catch (error) {
      console.error('Error removing favorite shop:', error);
    }
  },

  isFavorite: (shopId: string) => {
    return get().favorites.some((f) => f.shop_id === shopId);
  },
}));

/**
 * Hook to manage favorite shops with auto-fetch on mount
 */
export function useFavoriteShops() {
  const store = useFavoriteShopsStore();
  const { user } = useAuth();

  // Auto-fetch favorites when user is available
  React.useEffect(() => {
    if (user) {
      store.fetchFavorites(user.id);
    }
  }, [user]);

  return {
    ...store,
    /** Toggle favorite status for a given shop */
    toggleFavorite: async (shopId: string) => {
      if (!user) return;

      if (store.isFavorite(shopId)) {
        await store.removeFavorite(user.id, shopId);
      } else {
        await store.addFavorite(user.id, shopId);
      }
    },
  };
}

// Import React and useAuth
import React from 'react';
import { useAuth } from './useAuth';
